import columnDefinitionEnum from '../column-definition.enum';
import { Observer } from 'mobx-react-lite';
import { Tooltip } from '../../common/components/tooltip';
import { RawQueueRowData } from '../store/types-2';
import { useQueueStore } from '../context/use-queue-store';
import { UnitQueueRowData } from '../../socket/stream-types';
import { Flex } from '@cad-arc';
import { QueueCellProvider } from './queue-cell-context';
import { BaseQueueCell } from './base-queue-cell';
import { getEventQueueRowData } from '../utils/get-event-queue-row-data';
import { useContextMenuStateWrite } from '../context/use-context-menu-state';
import { ColumnDefinitionEnumType } from '@mark43/cad-api';
import { colors } from '../../colors/colors';
import { Mask } from '../../common/components/datadog-privacy';

function PinToPrimaryLeftBorder(props: React.HTMLAttributes<HTMLDivElement>) {
    return (
        <div
            {...props}
            style={{
                height: '100%',
                width: '18px',
                minWidth: '18px',
                borderRight: '3px solid ' + colors.backupUnitBorder,
                ...props.style,
            }}
        >
            {props.children}
        </div>
    );
}

export function QueueCell({
    columnDefinitionName,
    data,
    index,
}: {
    columnDefinitionName: ColumnDefinitionEnumType;
    data: RawQueueRowData;
    index: number;
}): React.ReactElement {
    const queueStore = useQueueStore();
    const columnDefinitionValue = columnDefinitionEnum[columnDefinitionName];
    const contextMenuStateWrite = useContextMenuStateWrite();

    if (!columnDefinitionValue) {
        throw new Error(
            `Unexpectedly did not find a value for entry ${columnDefinitionName} in columnDefinitionEnum`
        );
    }
    if (columnDefinitionValue.component) {
        const Cell = columnDefinitionValue.component;
        return (
            <Flex style={{ gap: 0, width: '100%', height: '100%' }}>
                {index === 0 &&
                queueStore.workspacePanel.queue.pinBackupsToPrimaryUnits &&
                (data as UnitQueueRowData).backingUpUnitQueueView ? (
                    <PinToPrimaryLeftBorder />
                ) : null}
                <QueueCellProvider
                    index={index}
                    eventId={getEventQueueRowData(data)?.id ?? -1}
                    columnDefinitionValue={columnDefinitionValue}
                >
                    <Mask>
                        {/* @ts-expect-error Our cell component is generic and the intersection within our enum will always type `columnDefinition` as `never`. Without extremely verbose code we cannot improve this so we are knowingly taking on tech debt here. */}
                        <Cell data={data} columnDefinition={columnDefinitionValue} />
                    </Mask>
                </QueueCellProvider>
            </Flex>
        );
    } else {
        const isBackupRow =
            queueStore.workspacePanel.queue.pinBackupsToPrimaryUnits &&
            !!(data as UnitQueueRowData).backingUpUnitQueueView;
        return (
            <Flex style={{ gap: 0, width: '100%', height: '100%' }}>
                {index === 0 && isBackupRow ? <PinToPrimaryLeftBorder /> : null}
                <QueueCellProvider
                    index={index}
                    eventId={getEventQueueRowData(data)?.id ?? -1}
                    columnDefinitionValue={columnDefinitionValue}
                >
                    <Observer>
                        {(): React.ReactElement => {
                            const { contextMenuKind } = columnDefinitionEnum[columnDefinitionName];
                            const eventQueueRowData = getEventQueueRowData(data);
                            // We never load a "cell specific context menu" for the scheduled events queue.
                            const onContextMenu =
                                queueStore.workspacePanel.queue.dataEntityType !==
                                    'SCHEDULED_AGENCY_EVENT' &&
                                contextMenuKind &&
                                eventQueueRowData
                                    ? () =>
                                          contextMenuStateWrite({
                                              kind: contextMenuKind,
                                              entityId: eventQueueRowData.id,
                                          })
                                    : undefined;

                            return isBackupRow &&
                                columnDefinitionValue.omitWhenBackupPinnedToPrimary ? (
                                <BaseQueueCell onContextMenu={onContextMenu} />
                            ) : (
                                <Mask>
                                    <BaseQueueCell
                                        onContextMenu={onContextMenu}
                                        alignment={
                                            columnDefinitionValue.contentAlignment
                                                ? columnDefinitionValue.contentAlignment
                                                : 'LEFT'
                                        }
                                    >
                                        <Tooltip
                                            overlay={
                                                columnDefinitionValue.getTip
                                                    ? columnDefinitionValue.getTip(data)
                                                    : null
                                            }
                                        >
                                            <span>{columnDefinitionValue.getFlatValue(data)}</span>
                                        </Tooltip>
                                    </BaseQueueCell>
                                </Mask>
                            );
                        }}
                    </Observer>
                </QueueCellProvider>
            </Flex>
        );
    }
}
